import { useEffect, useState } from 'react';
import { getCotizaciones, createCotizacion, updateEstatusCotizacion, getProspectos, getAseguradoras, getRamos } from '../services/api';
import Modal from '../components/ui/Modal';

const ESTATUS_COLORS: Record<string, string> = {
  Borrador:  'bg-gray-100 text-gray-600',
  Enviada:   'bg-blue-100 text-blue-700',
  Aceptada:  'bg-green-100 text-green-700',
  Rechazada: 'bg-red-100 text-red-700',
};

const EMPTY_FORM = {
  ramo: '', aseguradora: '', primaAnual: '', sumaAsegurada: '',
  detalles: '', prospectoId: '',
};

export default function Cotizaciones() {
  const [cotizaciones, setCotizaciones] = useState<any[]>([]);
  const [aseguradoras, setAseguradoras] = useState<any[]>([]);
  const [ramos, setRamos] = useState<any[]>([]);
  const [prospectos, setProspectos] = useState<any[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = () => getCotizaciones().then(r => setCotizaciones(r.data));

  useEffect(() => {
    load();
    getAseguradoras().then(r => setAseguradoras(r.data));
    getRamos().then(r => setRamos(r.data));
    getProspectos().then(r => setProspectos(r.data));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await createCotizacion({
        ramo: form.ramo,
        aseguradora: form.aseguradora,
        primaAnual: Number(form.primaAnual),
        sumaAsegurada: Number(form.sumaAsegurada),
        detalles: form.detalles || null,
        prospectoId: Number(form.prospectoId),
      });
      setShowModal(false);
      setForm(EMPTY_FORM);
      load();
    } catch {
      setError('Error al guardar. Verifica los datos.');
    } finally {
      setSaving(false);
    }
  };

  const handleEstatus = async (id: number, estatus: string) => {
    await updateEstatusCotizacion(id, estatus);
    setCotizaciones(prev => prev.map(c => c.id === id ? { ...c, estatus } : c));
  };

  const field = (key: keyof typeof EMPTY_FORM) => ({
    value: form[key],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm(f => ({ ...f, [key]: e.target.value })),
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Cotizaciones</h2>
        <button
          onClick={() => { setShowModal(true); setForm(EMPTY_FORM); setError(''); }}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-primary-700"
        >
          + Nueva cotización
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
            <tr>
              <th className="px-4 py-3 text-left">Prospecto</th>
              <th className="px-4 py-3 text-left">Ramo</th>
              <th className="px-4 py-3 text-left">Aseguradora</th>
              <th className="px-4 py-3 text-left">Prima anual</th>
              <th className="px-4 py-3 text-left">Suma asegurada</th>
              <th className="px-4 py-3 text-left">Fecha</th>
              <th className="px-4 py-3 text-left">Estatus</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {cotizaciones.map(c => (
              <tr key={c.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-800">{c.prospecto?.nombre}</td>
                <td className="px-4 py-3 text-gray-600">{c.ramo}</td>
                <td className="px-4 py-3 text-gray-600">{c.aseguradora}</td>
                <td className="px-4 py-3 text-gray-600">${c.primaAnual.toLocaleString()}</td>
                <td className="px-4 py-3 text-gray-600">${c.sumaAsegurada.toLocaleString()}</td>
                <td className="px-4 py-3 text-gray-600">
                  {new Date(c.creadoEn).toLocaleDateString('es-MX')}
                </td>
                <td className="px-4 py-3">
                  <select value={c.estatus} onChange={e => handleEstatus(c.id, e.target.value)}
                    className={`text-xs px-2 py-1 rounded-full border-0 font-medium ${ESTATUS_COLORS[c.estatus]}`}>
                    {Object.keys(ESTATUS_COLORS).map(s => <option key={s}>{s}</option>)}
                  </select>
                </td>
              </tr>
            ))}
            {cotizaciones.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-400">No hay cotizaciones</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <Modal title="Nueva cotización" onClose={() => setShowModal(false)} size="md">
          <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prospecto *</label>
              <select {...field('prospectoId')} required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">Seleccionar prospecto...</option>
                {prospectos.map((p: any) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ramo *</label>
              <select {...field('ramo')} required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">Seleccionar...</option>
                {ramos.map((r: any) => <option key={r.id} value={r.nombre}>{r.icono} {r.nombre}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Aseguradora *</label>
              <select {...field('aseguradora')} required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">Seleccionar...</option>
                {aseguradoras.map((a: any) => <option key={a.id} value={a.nombre}>{a.nombre}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prima anual ($) *</label>
              <input type="number" {...field('primaAnual')} required min={1} step={0.01}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Suma asegurada ($) *</label>
              <input type="number" {...field('sumaAsegurada')} required min={0} step={1000}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Detalles / Coberturas</label>
              <textarea {...field('detalles')} rows={2}
                placeholder="Cobertura amplia, deducible $5,000, suma asegurada vehicular..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" />
            </div>

            {error && <p className="col-span-2 text-red-500 text-sm">{error}</p>}

            <div className="col-span-2 flex gap-2 justify-end pt-2 border-t border-gray-100">
              <button type="button" onClick={() => setShowModal(false)}
                className="px-4 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                Cancelar
              </button>
              <button type="submit" disabled={saving}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 disabled:opacity-60">
                {saving ? 'Guardando...' : 'Guardar cotización'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
